// Fee Proposal Templates admin — library of starting-point template files
// (PDF/Word, supplied by Priority Risk) staff can pick from when drafting a
// new enquiry's fee proposal. Upload/delete: director/administrator only.
import { useEffect, useRef, useState } from "react";
import { Plus, Upload, Download, Trash2, X, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["director", "administrator"]);

function formatBytes(n) {
    if (!n) return "0 KB";
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FeeProposalTemplatesPage() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [uploading, setUploading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const fileRef = useRef(null);
    const isAdmin = user && ADMIN_ROLES.has(user.role);

    function load() {
        setLoading(true);
        api.get("/fee-proposal-templates").then((r) => setRows(r.data))
            .catch(() => toast.error("Could not load templates"))
            .finally(() => setLoading(false));
    }
    useEffect(() => { load(); }, []);

    function openFilePicker() {
        if (!name.trim()) { toast.error("Give the template a name first"); return; }
        fileRef.current?.click();
    }

    async function onPickFile(e) {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", name.trim());
        if (description.trim()) fd.append("description", description.trim());
        setUploading(true);
        try {
            await api.post("/fee-proposal-templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Template uploaded");
            setAddOpen(false); setName(""); setDescription("");
            load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
        finally { setUploading(false); }
    }

    async function downloadTpl(t) {
        setBusyId(t.id);
        try {
            const res = await api.get(`/fee-proposal-templates/${t.id}/file`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = t.filename || t.name;
            a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Could not download template"); }
        finally { setBusyId(null); }
    }

    async function remove(t) {
        if (!window.confirm(`Remove "${t.name}"?`)) return;
        setBusyId(t.id);
        try { await api.delete(`/fee-proposal-templates/${t.id}`); toast.success("Removed"); load(); }
        catch { toast.error("Delete failed"); }
        finally { setBusyId(null); }
    }

    return (
        <div data-testid="fee-proposal-templates-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent,#C75A1B)", color: "#6F6A64" }}>Admin · Settings</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Fee Proposal Templates</h2>
                    <p className="text-[13px] mt-1 text-[#6F6A64]">Starting-point fee proposal files staff can pick from on a new enquiry. Upload your own PDF/Word templates here.</p>
                </div>
                {isAdmin && (
                    <button data-testid="fee-proposal-template-new" onClick={() => setAddOpen(true)}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        <Plus size={15} /> New template
                    </button>
                )}
            </div>

            {loading ? (
                <div className="py-16 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
            ) : rows.length === 0 ? (
                <div className="py-16 text-center text-[#9A968F] border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>
                    No templates uploaded yet.{isAdmin ? " Add the fee proposal template files your client provides." : ""}
                </div>
            ) : (
                <div className="bg-white border rounded-[12px] shadow-brand-sm divide-y" style={{ borderColor: "#E5E0DA" }}>
                    {rows.map((t) => (
                        <div key={t.id} data-testid={`fee-proposal-template-row-${t.id}`} className="flex items-center gap-3 px-4 py-3">
                            <FileText size={15} className="text-[#2A5DB0] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-[#1F1F1F] text-[13.5px] truncate">{t.name}</div>
                                <div className="text-[11px] text-[#9A968F]">{t.description ? `${t.description} · ` : ""}{formatBytes(t.size)}</div>
                            </div>
                            <button data-testid={`fee-proposal-template-download-${t.id}`} disabled={busyId === t.id} onClick={() => downloadTpl(t)} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}><Download size={12} /> Download</button>
                            {isAdmin && (
                                <button data-testid={`fee-proposal-template-delete-${t.id}`} disabled={busyId === t.id} onClick={() => remove(t)} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-[#B33A2E] hover:bg-[#FBE8E5]"><Trash2 size={12} /></button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {addOpen && isAdmin && (
                <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
                    <div data-testid="fee-proposal-template-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-md border" style={{ borderColor: "#E5E0DA" }}>
                        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                            <h3 className="text-base font-bold text-[#1F1F1F]">New template</h3>
                            <button onClick={() => { setAddOpen(false); setName(""); setDescription(""); }} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <input data-testid="fee-proposal-template-name" value={name} onChange={(e) => setName(e.target.value)}
                                placeholder="Template name (e.g. Standard CDM Fee Proposal)"
                                className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]" />
                            <input data-testid="fee-proposal-template-description" value={description} onChange={(e) => setDescription(e.target.value)}
                                placeholder="Description (optional)"
                                className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]" />
                            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" hidden onChange={onPickFile} data-testid="fee-proposal-template-file-input" />
                            <button data-testid="fee-proposal-template-choose-file" disabled={uploading} onClick={openFilePicker}
                                className="w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold py-2 rounded-[7px] text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Choose file &amp; upload
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
